import React, { useState, useEffect } from 'react';
import { useUser } from '../contexts/UserContext';
import axios from 'axios';
import { useMessage } from '../contexts/MessageContext'; // Importar el hook de mensajes
import { getCategorias, getEtiquetasPorCategoria } from '../config/categoriasConfig';
import ImageUploadField from './ImageUploadField';
import { API_BASE_URL } from '../config/apiConfig';

const FormularioDirecto = () => {
  const { showMessage } = useMessage(); // Obtener la función de mensajes del contexto
  const categoriasOrdenadas = getCategorias();
  const [formData, setFormData] = useState({
    pregunta: '',
    respuesta_correcta: '',
    respuesta_falsa1: '',
    respuesta_falsa2: '',
    respuesta_falsa3: '',
    categoria: '',
    etiqueta: '',
    dificultad: 'facil',
    explicacion: '',
    imagen: '',
    imagen_temp_token: '',
    autor: ''
  });

  // Obtener usuario del contexto global
  const { user } = useUser();
  const [showExplicacion, setShowExplicacion] = useState(false);

  useEffect(() => {
    // Si el usuario está logueado y el campo autor está vacío, autocompletar
    if (user && (user.nickname || user.username)) {
      const nombre = user.nickname || user.username;
      setFormData(prev => prev.autor ? prev : { ...prev, autor: nombre });
    }
  }, [user]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_BASE_URL}/agregar_pregunta.php`, {
        ...formData,
        aprobada: 1
      });
      showMessage('¡Pregunta agregada directamente!', 'success');
      setFormData({
        pregunta: '',
        respuesta_correcta: '',
        respuesta_falsa1: '',
        respuesta_falsa2: '',
        respuesta_falsa3: '',
        categoria: '',
        etiqueta: '',
        dificultad: 'facil',
        explicacion: '',
        imagen: '',
        imagen_temp_token: '',
        autor: ''
      });
    } catch (error) {
      console.error('Error al agregar pregunta:', error);
      // Rechazo automático por duplicado
      if (error?.response?.data?.duplicada) {
        showMessage(
          error.response.data.message || 'Esta pregunta es igual o muy similar a una ya existente y ha sido rechazada automáticamente.',
          'warning'
        );
        return;
      }
      showMessage('Hubo un error al agregar la pregunta.', 'error');
    }
  };

  return (
    <form onSubmit={handleSubmit} className="w-full max-w-4xl mx-auto flex flex-col gap-8 px-2 md:px-0 py-6 dark:text-white transition-colors duration-300">
      {/* Fila superior: pregunta y respuestas en 2 columnas */}
      <div className="flex flex-col lg:flex-row gap-6 lg:gap-8 w-full rounded-2xl border border-slate-200/70 dark:border-slate-700/80 bg-gradient-to-br from-white/85 to-sky-50/50 dark:from-slate-900/30 dark:to-slate-800/30 p-3 sm:p-4">
        {/* Pregunta */}
        <div className="flex-1 flex flex-col">
          <label className="font-semibold text-base sm:text-lg mb-2 dark:text-white" htmlFor="pregunta">Pregunta *</label>
          <textarea
            id="pregunta"
            name="pregunta"
            value={formData.pregunta}
            onChange={handleChange}
            required
            className="w-full min-h-[150px] max-h-[350px] resize-y p-3 sm:p-5 text-base sm:text-lg rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500 shadow-sm transition-all duration-200"
            style={{ fontWeight: 500, lineHeight: '1.5', boxShadow: '0 2px 8px 0 rgb(0 0 0 / 0.03)' }}
            placeholder="Escribe la pregunta aquí..."
          />
        </div>
        {/* Respuestas en 2 columnas */}
        <div className="flex-1 flex flex-col">
          <label className="font-semibold text-base sm:text-lg mb-2 dark:text-white">Respuestas</label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 sm:gap-4">
            <input
              type="text"
              name="respuesta_correcta"
              value={formData.respuesta_correcta}
              onChange={handleChange}
              required
              className="p-3 rounded-lg border-4 !border-green-500 focus:!border-green-600 focus:!ring-2 focus:!ring-green-400 bg-white dark:bg-gray-800 font-semibold shadow-sm transition-all duration-200"
              placeholder="Respuesta correcta"
              style={{ boxShadow: '0 2px 8px 0 rgb(0 255 0 / 0.03)' }}
            />
            <input
              type="text"
              name="respuesta_falsa1"
              value={formData.respuesta_falsa1}
              onChange={handleChange}
              required
              className="p-3 rounded-lg border-4 !border-red-500 focus:!border-red-600 focus:!ring-2 focus:!ring-red-400 bg-white dark:bg-gray-800 font-semibold shadow-sm transition-all duration-200"
              placeholder="Respuesta incorrecta 1"
            />
            <input
              type="text"
              name="respuesta_falsa2"
              value={formData.respuesta_falsa2}
              onChange={handleChange}
              required
              className="p-3 rounded-lg border-4 !border-red-500 focus:!border-red-600 focus:!ring-2 focus:!ring-red-400 bg-white dark:bg-gray-800 font-semibold shadow-sm transition-all duration-200"
              placeholder="Respuesta incorrecta 2"
            />
            <input
              type="text"
              name="respuesta_falsa3"
              value={formData.respuesta_falsa3}
              onChange={handleChange}
              required
              className="p-3 rounded-lg border-4 !border-red-500 focus:!border-red-600 focus:!ring-2 focus:!ring-red-400 bg-white dark:bg-gray-800 font-semibold shadow-sm transition-all duration-200"
              placeholder="Respuesta incorrecta 3"
            />
          </div>
        </div>
      </div>

      {/* Fila secundaria: categoría/etiqueta y dificultad/autor */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 w-full rounded-2xl border border-slate-200/70 dark:border-slate-700/80 bg-gradient-to-br from-white/85 to-amber-50/40 dark:from-slate-900/30 dark:to-slate-800/30 p-3 sm:p-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
          <div className="flex flex-col">
            <label className="font-medium mb-1 dark:text-gray-200">Categoría *</label>
            <select
              name="categoria"
              value={formData.categoria}
              onChange={handleChange}
              required
              className="w-full p-2.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-200"
            >
              <option value="" className="dark:bg-gray-700 dark:text-white">Seleccionar categoría</option>
              {categoriasOrdenadas.map(categoria => (
                <option key={categoria.id} value={categoria.id} className="dark:bg-gray-700 dark:text-white">{categoria.nombre}</option>
              ))}
            </select>
          </div>
          <div className="flex flex-col">
            <label className="font-medium mb-1 dark:text-gray-200">Etiqueta *</label>
            <select
              name="etiqueta"
              value={formData.etiqueta}
              onChange={handleChange}
              required
              disabled={!formData.categoria}
              className="w-full p-2.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-200 disabled:opacity-50"
            >
              <option value="" className="dark:bg-gray-700 dark:text-white">Seleccionar etiqueta</option>
              {formData.categoria && getEtiquetasPorCategoria(formData.categoria)?.map(etiqueta => (
                <option key={etiqueta} value={etiqueta} className="dark:bg-gray-700 dark:text-white">{etiqueta}</option>
              ))}
            </select>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="flex flex-col">
            <label className="font-medium mb-1 dark:text-gray-200">Dificultad *</label>
            <select
              name="dificultad"
              value={formData.dificultad}
              onChange={handleChange}
              required
              className="w-full p-2.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-200"
            >
              <option value="facil" className="dark:bg-gray-700 dark:text-white">Fácil</option>
              <option value="medio" className="dark:bg-gray-700 dark:text-white">Medio</option>
              <option value="dificil" className="dark:bg-gray-700 dark:text-white">Difícil</option>
            </select>
          </div>
          <div className="flex flex-col">
            <label className="font-medium mb-1 dark:text-gray-200">Autor</label>
            <input
              type="text"
              name="autor"
              value={formData.autor}
              onChange={handleChange}
              className="w-full p-2.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-200"
              placeholder="Autor de la pregunta"
              autoComplete="username"
            />
          </div>
        </div>
      </div>

      {/* Explicación + imagen (bloque lateral derecho) */}
      <div className="w-full rounded-2xl border border-slate-200/70 dark:border-slate-700/80 bg-gradient-to-br from-white/85 to-indigo-50/40 dark:from-slate-900/30 dark:to-slate-800/30 p-3 sm:p-4">
        <button
          type="button"
          className="flex items-center gap-2 text-blue-600 dark:text-blue-300 font-medium focus:outline-none hover:underline mb-2 select-none"
          onClick={() => setShowExplicacion((v) => !v)}
          aria-expanded={showExplicacion}
        >
          <span className={showExplicacion ? 'rotate-90 transition-transform' : 'transition-transform'}>
            ▼
          </span>
          {showExplicacion ? 'Ocultar explicación' : 'Añadir explicación'}
        </button>
        <div className={`overflow-hidden transition-all duration-200 ${showExplicacion ? 'max-h-[900px] opacity-100' : 'max-h-0 opacity-0'}`}>
          <div className="grid grid-cols-1 md:grid-cols-[1fr_1.4fr] gap-4 mt-2 items-stretch">
            <ImageUploadField
              value={formData.imagen}
              tempToken={formData.imagen_temp_token}
              flatWhenEmpty
              onChange={(imagen, imagenTempToken) => setFormData(prev => ({
                ...prev,
                imagen,
                imagen_temp_token: imagenTempToken
              }))}
            />
            <textarea
              name="explicacion"
              value={formData.explicacion}
              onChange={handleChange}
              className="w-full h-full min-h-[170px] p-3 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white transition-colors duration-300 resize-y"
              rows={4}
              placeholder="Explicación opcional de la pregunta"
            />
          </div>
        </div>
      </div>

      <div className="w-full flex justify-stretch sm:justify-end mt-2">
        <button
          type="submit"
          className="w-full sm:w-auto px-8 py-3 rounded-lg bg-blue-600 text-white font-semibold text-lg shadow-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-400 transition-all duration-200 dark:bg-blue-700 dark:hover:bg-blue-800 flex items-center justify-center gap-2"
          aria-label="Enviar Pregunta"
        ><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" width="28" height="28" fill="currentColor">
            <g transform="translate(0,512) scale(0.1,-0.1)" stroke="none">
              <path d="M1463 3845 c-62 -27 -63 -35 -63 -435 0 -406 1 -414 67 -445 46 -22 103 -7 132 34 19 26 21 46 23 264 l3 235 493 -436 493 -436 -488 -488 c-268  -268 -491 -488 -495 -488 -5 0 -8 171 -8 381 l0 381 -34 34 c-39 39 -77 44 -127 18 -41 -21 -59 -68 -59 -154 l0 -70 -272 0 c-150 0 -290 -3 -311 -7 -23 -3 -50 -18 -68 -35 -56 -57 -29 -156 49 -178 21 -6 157 -10 320 -10 l282 0 0 -326 c0 -269 3 -332 15 -358 31 -66 -117 -61 1825 -64 1262 -2 1776 0 1803 8 25 7 45 22 57 43 19 31 20 60 20 1240 0 1310 2 1251 -52 1287 -33 22 -3555 27 -3605 5z m3435 -1202 c1 -546 -1 -993 -5 -993 -12 0 -963 954 -962 965 0 5 158 143 350 305 193 162 364 310 380 328 22 24 29 43 29 72 0 83 -95 136 -165 92 -16 -11 -306 -254 -644 -541 -338 -287 -618 -520 -622 -519 -10 4 -1424 1255 -1440 1274 -10 12 211 14 1532 12 l1544 -3 3 -992z m-1908 -354 c225  -199 225 -199 269 -199 46 0 44 -1 290 208 l204 174 489 -489 c268 -268 488 -491 488 -495 0 -5 -661 -8 -1470 -8 -808 0 -1470 3 -1470 7 0 5 221 229 492 500 485 485 493 492 513 474 11 -10 99 -87 195 -172z"/>
              <path d="M263 3416 c-83 -37 -87 -153 -6 -197 21 -11 107 -14 472 -14 416 0 449 1 475 18 35 23 58 76 50 116 -3 16 -19 42 -36 58 l-29 28 -447 2 c-365 2 -453 0 -479 -11z"/>
              <path d="M86 2810 c-69 -21 -101 -90 -72 -152 32 -67 -37 -63 1021 -66 1079 -3 1010 -7 1049 71 16 34 17 42 5 74 -8 20 -27 45 -42 57 -28 21 -29 21 -980 23 -524 1 -965 -2 -981 -7z"/>
              <path d="M83 1763 c-32 -6 -72 -51 -79 -88 -7 -41 11 -81 49 -109 28 -20 40 -21 446 -24 452 -3 477 -1 514 49 25 34 25 94 0 128 -36 48 -58 50 -498 49 -226 0 -420 -3 -432 -5z"/>
            </g>
          </svg>
        </button>
      </div>
    </form>
  );
};

export default FormularioDirecto;
